// Every word the web client shows comes from here (RFC-043 "Web client"):
// i18next over src/locales/<language>/<namespace>.json. A key names its
// namespace, t("files:fileDelete"); a word picked by a value is
// word("common:role", r); a phrase that picks between keys is in phrases.ts.
// Nothing else in src/ builds a user-visible sentence, and nothing calls t()
// outside a function: what it returned would not follow a change of language.
import i18n, { type Resource } from "i18next";
import { initReactI18next } from "react-i18next";
import { keep, readKept } from "../kept";
import { bundles, namespaces, type Resources } from "./resources";

declare module "i18next" {
  interface CustomTypeOptions {
    defaultNS: "common";
    resources: Resources;
  }
}

/** The languages offered, each named in its own words. */
export const languages = [
  { code: "en", name: "English" },
  { code: "ko", name: "한국어" },
] as const;
export type Language = (typeof languages)[number]["code"];

const StoreKey = "share.lang";
const isLanguage = (s: string | null | undefined): s is Language => languages.some((l) => l.code === s);

/** The language chosen in this browser, else the browser's own, else English. */
function initial(): Language {
  const kept = readKept(StoreKey);
  if (isLanguage(kept)) return kept;
  const asked = typeof navigator === "undefined" ? [] : (navigator.languages ?? [navigator.language]);
  for (const tag of asked) {
    const base = tag?.toLowerCase().split("-")[0];
    if (isLanguage(base)) return base;
  }
  return "en";
}

const resources = Object.fromEntries(
  Object.entries(bundles).map(([lang, nss]) => [lang, Object.fromEntries(Object.entries(nss).filter(([ns]) => ns !== "docs"))]),
) as Resource;

void i18n.use(initReactI18next).init({
  resources,
  lng: initial(),
  fallbackLng: "en",
  supportedLngs: languages.map((l) => l.code),
  ns: namespaces,
  defaultNS: "common",
  initAsync: false,
  interpolation: { escapeValue: false },
});
if (typeof document !== "undefined") document.documentElement.lang = i18n.language;

export const t = i18n.t.bind(i18n) as typeof i18n.t;

/** The language shown now. */
export function language(): Language {
  return isLanguage(i18n.language) ? i18n.language : "en";
}

/** The languages the main server's profile offers (byj-1wv.114.13); null, every one. */
let offeredCodes: Language[] | null = null;

/** The languages offered here: the app's, as far as the profile offers them. */
export function offeredLanguages() {
  return languages.filter((l) => !offeredCodes || offeredCodes.includes(l.code));
}

/**
 * Offer only `codes` of the app's languages, as the profile says (none said,
 * every one): the app is shown in its first when the language now is not one
 * of them, and that is not kept as this browser's choice.
 */
export function offerLanguages(codes?: string[]) {
  const known = codes?.filter(isLanguage);
  offeredCodes = known && known.length > 0 ? known : null;
  const first = offeredCodes?.[0];
  if (first && !offeredCodes?.includes(language())) {
    void i18n.changeLanguage(first);
    if (typeof document !== "undefined") document.documentElement.lang = first;
  }
}

/** Show the app in `lang`, and keep it as this browser's choice. */
export function setLanguage(lang: Language) {
  keep(StoreKey, lang);
  void i18n.changeLanguage(lang);
  document.documentElement.lang = lang;
}

/** A key whose value is a map of words: common:role, shares:kindName, … */
type WordMap = {
  [N in keyof Resources]: { [K in keyof Resources[N]]: Resources[N][K] extends string ? never : `${N & string}:${K & string}` }[keyof Resources[N]];
}[keyof Resources];

/** The word for `value` in the map at `key`, or undefined for a value it does not name. */
export function word(key: WordMap, value: string): string | undefined {
  const full = `${key}.${value}`;
  return i18n.exists(full) ? (i18n.t as (k: string) => string)(full) : undefined;
}
